import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonButton,
    IonText,
    IonAlert,
    IonModal,
} from "@ionic/react";

// Question Interface
interface QuestionData {
    image: string;
    sentence: string;
    correctOption: string;
    options: string[];
}


const ThisThatTheseThose: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctOption) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                console.log(nextIndex)
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
                setShowImage(false);
                setSelectedOption(null);
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'ThisThatTheseThose.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/ThisThatTheseThose.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "This That These Those Worksheets for UKG | Demonstrative Pronouns Practice"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Help UKG children understand and use "this," "that," "these," and "those" with engaging activities at Mama’s Guidance, designed to strengthen grammar and language skills.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>This, That, These, Those</h1>

                                    <p>
                                        Understanding how to use <strong>this, that, these, and those</strong> correctly is an important grammar skill for UKG students. These words are called <strong>demonstrative pronouns</strong> and are used to point out specific objects. Our <strong>UKG worksheets</strong> help children learn these concepts with fun and colorful picture-based activities.
                                    </p>

                                    <h2>📘 What Are Demonstrative Pronouns?</h2>
                                    <ul>
                                        <li><strong>This</strong> – used for a single object that is near</li>
                                        <li><strong>That</strong> – used for a single object that is far</li>
                                        <li><strong>These</strong> – used for more than one object that is near</li>
                                        <li><strong>Those</strong> – used for more than one object that is far</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Differentiate <strong>between near and far</strong> objects</li>
                                        <li>Understand <strong>singular vs. plural</strong> usage</li>
                                        <li>Use <strong>this, that, these, and those</strong> in correct sentences</li>
                                        <li>Enhance grammar, observation, and reading skills</li>
                                    </ul>

                                    <h2>🖍️ Activities in the Worksheets</h2>
                                    <ul>
                                        <li>Circle the correct word (<strong>this/that/these/those</strong>)</li>
                                        <li>Match sentences with pictures</li>
                                        <li>Fill in the blanks with appropriate demonstrative pronouns</li>
                                        <li>Identify objects that are near or far</li>
                                        <li>Use pictures to form correct grammar sentences</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                    <ul>
                                        <li>Use real objects around the home to demonstrate each word</li>
                                        <li>Practice with toy sets: <strong>“This is my car”, “Those are blocks”</strong></li>
                                        <li>Encourage children to speak in full sentences using these words</li>
                                        <li>Ask simple questions like <strong>“What is this?”</strong> or <strong>“What are those?”</strong></li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/english-language/articles"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Articles</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/english-language/one-and-many"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>One and Many</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/english-language/rhyming-words"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Rhyming Words</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Use pointing games at home! Say <strong>“this is my pencil”</strong> while pointing to one nearby, and <strong>“those are your shoes”</strong> when pointing at far objects. The more they practice, the better they learn.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>This, That, These, Those</h1>

                                <p>
                                    Understanding how to use <strong>this, that, these, and those</strong> correctly is an important grammar skill for UKG students. These words are called <strong>demonstrative pronouns</strong> and are used to point out specific objects. Our <strong>UKG worksheets</strong> help children learn these concepts with fun and colorful picture-based activities.
                                </p>

                                <h2>📘 What Are Demonstrative Pronouns?</h2>
                                <ul>
                                    <li><strong>This</strong> – used for a single object that is near</li>
                                    <li><strong>That</strong> – used for a single object that is far</li>
                                    <li><strong>These</strong> – used for more than one object that is near</li>
                                    <li><strong>Those</strong> – used for more than one object that is far</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Differentiate <strong>between near and far</strong> objects</li>
                                    <li>Understand <strong>singular vs. plural</strong> usage</li>
                                    <li>Use <strong>this, that, these, and those</strong> in correct sentences</li>
                                    <li>Enhance grammar, observation, and reading skills</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Circle the correct word (<strong>this/that/these/those</strong>)</li>
                                    <li>Match sentences with pictures</li>
                                    <li>Fill in the blanks with appropriate demonstrative pronouns</li>
                                    <li>Identify objects that are near or far</li>
                                    <li>Use pictures to form correct grammar sentences</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Use real objects around the home to demonstrate each word</li>
                                    <li>Practice with toy sets: <strong>“This is my car”, “Those are blocks”</strong></li>
                                    <li>Encourage children to speak in full sentences using these words</li>
                                    <li>Ask simple questions like <strong>“What is this?”</strong> or <strong>“What are those?”</strong></li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Use pointing games at home! Say <strong>“this is my pencil”</strong> while pointing to one nearby, and <strong>“those are your shoes”</strong> when pointing at far objects. The more they practice, the better they learn.
                                </p>
                            </article>
                            <h4>Fill in the blank with the correct option:</h4>

                            <p className="mt-5 clearfix">
                                {
                                    questions[currentIndex]?.correctOption === 'This' || questions[currentIndex]?.correctOption === 'These'
                                        ?
                                        <>
                                            <span className="float-start">
                                                <img className="img-thumbnail" width="20%" src={process.env.REACT_APP_API_URL + questions[currentIndex]?.image} />
                                                <IonText style={{ marginLeft: '30px' }}>{questions[currentIndex]?.sentence}</IonText>
                                            </span>
                                        </>
                                        :
                                        <></>
                                }


                                {
                                    questions[currentIndex]?.correctOption === 'That' || questions[currentIndex]?.correctOption === 'Those'
                                        ?
                                        <>
                                            <IonText style={{ marginLeft: '30px' }}>{questions[currentIndex]?.sentence}</IonText>
                                            <span className="float-end">
                                                <img className="img-thumbnail" width="20%" src={process.env.REACT_APP_API_URL + questions[currentIndex]?.image} />
                                            </span>
                                        </>
                                        :
                                        <></>
                                }
                            </p>
                            <div className="mb-5">
                                <IonRadioGroup
                                    value={selectedOption}
                                    onIonChange={(e) => handleSubmit(e.detail.value)}
                                >
                                    {questions[currentIndex]?.options.map((option, index) => (
                                        <IonItem
                                            className={` ${selectedOption === option
                                                ? option === questions[currentIndex]?.correctOption
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}
                                            key={index}>
                                            <IonLabel>{option}</IonLabel>
                                            <IonRadio slot="start" value={option} />
                                        </IonItem>
                                    ))}
                                </IonRadioGroup>
                            </div>
                        </>

                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default ThisThatTheseThose;
